/** @jsx jsx */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { jsx, css } from '@compiled/react';
import { graphql, useFragment } from 'react-relay';
import Button from '@atlaskit/button';
import { IconButton } from '@atlaskit/button/new';
import AddIcon from '@atlaskit/icon/core/migration/add--editor-add';
import ChevronDownIcon from '@atlaskit/icon/utility/migration/chevron-down';
import ChevronUpIcon from '@atlaskit/icon/utility/migration/chevron-up';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { JiraSiteAri } from '@atlassian/ari/jira/site';
import { JiraEntryPointContainer } from '@atlassian/jira-entry-point-container/src/index.tsx';
import { useEntryPoint } from '@atlassian/jira-entry-point/src/controllers/use-entry-point/index.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { useIssueKey } from '@atlassian/jira-issue-context-service/src/main.tsx';
import { DEFAULT_WORKSPACE } from '@atlassian/jira-issue-design-common/src/constants.tsx';
import { useDesignPanelStore } from '@atlassian/jira-issue-design-section-store/src/index.tsx';
import { WebhooksConfigurationLocation } from '@atlassian/jira-issue-get-design-updates-content/src/common/types.tsx';
import { SectionHeadingTitle } from '@atlassian/jira-issue-view-common/src/component/section-heading/section-heading-view.tsx';
import { fireUIAnalytics, type UIAnalyticsEvent } from '@atlassian/jira-product-analytics-bridge';
import { useProjectKey } from '@atlassian/jira-project-context-service/src/main.tsx';
import { useProjectPermissions } from '@atlassian/jira-project-permissions-service/src/main.tsx';
import type { heading_issueDesignSection_DesignsHeading$key } from '@atlassian/jira-relay/src/__generated__/heading_issueDesignSection_DesignsHeading.graphql.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { GetDesignUpdatesPopup } from '../../../common/ui/get-design-updates-popup/index.tsx';
import messages from './messages.tsx';
import { moreActionsDesignsModalEntryPoint } from './more-actions/entrypoint.tsx';

export type DesignsHeadingProps = {
	providerId: string;
	totalCount: number | undefined;
	isPanelExpanded: boolean;
	isCompactView: boolean;
	onDesignsPanelExpansion: (
		e: React.MouseEvent<HTMLElement>,
		analyticsEvent: UIAnalyticsEvent,
	) => void;
	providers: heading_issueDesignSection_DesignsHeading$key | null;
};

const DesignsHeading = ({
	providerId,
	totalCount,
	isPanelExpanded,
	isCompactView,
	onDesignsPanelExpansion,
	providers,
}: DesignsHeadingProps) => {
	const { formatMessage } = useIntl();
	const [isGetDesignUpdatesOpen, setIsGetDesignUpdatesOpen] = useState<boolean>(false);

	const issueKey = useIssueKey();
	const projectKey = useProjectKey(issueKey);
	const [{ canEditIssues }] = useProjectPermissions(projectKey);

	const [_, { setShouldShowAddDesignForm }] = useDesignPanelStore();

	const [designProviderIdForConfirmationDialog, setDesignProviderIdForConfirmationDialog] =
		useState<string | null>(null);

	const devOpsProvidersData = useFragment<heading_issueDesignSection_DesignsHeading$key>(
		graphql`
			fragment heading_issueDesignSection_DesignsHeading on DevOpsProviders {
				designProviders {
					id @required(action: THROW)
					configState(cloudId: $cloudId) @optIn(to: "Jira-config-state") {
						config {
							nodes {
								status
								workspaceId
							}
						}
					}
					supportedActions {
						getEntityByUrl
						checkAuth
					}
				}
			}
		`,
		providers,
	);

	const cloudId = useCloudId();
	const entryPointParams = useMemo(() => {
		const siteAri = JiraSiteAri.create({ siteId: cloudId }).toString();
		return { siteAri, issueKey, cloudId };
	}, [cloudId, issueKey]);

	const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
		moreActionsDesignsModalEntryPoint,
		entryPointParams,
	);

	const designProviders = useMemo(
		() => (devOpsProvidersData?.designProviders ?? []).filter(Boolean),
		[devOpsProvidersData],
	);

	const associateEntitySupportedProviders = designProviders.filter(
		({ supportedActions }) => supportedActions?.getEntityByUrl && supportedActions?.checkAuth,
	);

	const designProvider = useMemo(
		() => designProviders.find((provider) => provider?.id === providerId),
		[designProviders, providerId],
	);

	const isConfiguredOrNotInstalled = useMemo(
		() =>
			designProvider?.configState?.config?.nodes?.some(
				(node) =>
					node.workspaceId === DEFAULT_WORKSPACE &&
					(node.status === 'CONFIGURED' || node.status === 'NOT_INSTALLED'),
			) ?? true,
		[designProvider],
	);

	const shouldShowConfigButton = !isConfiguredOrNotInstalled && totalCount != null;

	const onAddDesignHandler = (
		e: React.MouseEvent<HTMLElement>,
		analyticsEvent: UIAnalyticsEvent,
	) => {
		if (!isPanelExpanded) {
			onDesignsPanelExpansion(e, analyticsEvent);
		}
		fireUIAnalytics(analyticsEvent, 'linkDesignPopup');

		setShouldShowAddDesignForm(true);
	};

	const onOpenGetDesignUpdatesHandler = useCallback(
		(e: React.SyntheticEvent, analyticsEvent: UIAnalyticsEvent) => {
			fireUIAnalytics(analyticsEvent, 'getLiveDesignsUpdatesPopup', {
				webhooksConfigurationLocation: WebhooksConfigurationLocation.SECTION,
			});

			setIsGetDesignUpdatesOpen(!isGetDesignUpdatesOpen);
		},
		[isGetDesignUpdatesOpen],
	);

	useEffect(() => {
		if (
			devOpsProvidersData &&
			devOpsProvidersData.designProviders &&
			!devOpsProvidersData.designProviders?.find((p) => p?.id === providerId)?.configState
		) {
			const error = new Error(`configState is NULL for provider ${providerId}`);

			fireErrorAnalytics({
				meta: {
					id: 'configStateEmpty',
					packageName: 'jiraIssueDesignSection',
					teamName: 'helios-headcoach',
				},
				error,
			});
		}
	}, [devOpsProvidersData, providerId]);

	// eslint-disable-next-line @atlassian/react-entrypoint/no-load-in-hooks
	useEffect(() => {
		if (isPanelExpanded) {
			entryPointActions.load();
		}
	}, [entryPointActions, isPanelExpanded]);

	const moreActionsRuntimeProps = useMemo(
		() => ({
			onClose: () => {
				setDesignProviderIdForConfirmationDialog(null);
			},
			designProviderIdForConfirmationDialog,
			providerId,
			isCompactView,
			onOpenGetDesignUpdatesHandler,
			setIsGetDesignUpdatesOpen,
			setDesignProviderIdForConfirmationDialog,
		}),
		[
			designProviderIdForConfirmationDialog,
			isCompactView,
			onOpenGetDesignUpdatesHandler,
			providerId,
		],
	);

	return (
		<div css={sectionHeadingStyles}>
			<SectionHeadingTitle data-testid="issue-design-section.ui.panel.heading.section-heading-title">
				{totalCount != null
					? formatMessage(messages.sectionHeading, {
							count: totalCount,
						})
					: formatMessage(messages.sectionHeadingNoCount)}
			</SectionHeadingTitle>
			<div css={sectionHeadingIconsStyles}>
				{totalCount != null && totalCount > 0 && (
					<>
						{shouldShowConfigButton && (
							<GetDesignUpdatesPopup
								isGetDesignUpdatesOpen={isGetDesignUpdatesOpen}
								setIsGetDesignUpdatesOpen={setIsGetDesignUpdatesOpen}
								onOpenGetDesignUpdatesHandler={onOpenGetDesignUpdatesHandler}
								shouldRenderTriggerButton={!isCompactView}
							/>
						)}
						<JiraEntryPointContainer
							entryPointReferenceSubject={entryPointReferenceSubject}
							runtimeProps={moreActionsRuntimeProps}
							id="jiraIssueBulkUnlinkDesignsModal"
							packageName="jiraIssueViewBase"
							errorFallback="unmount"
							teamName="helios-headcoach"
						/>
					</>
				)}
				{canEditIssues && associateEntitySupportedProviders.length > 0 && (
					<IconButton
						onClick={onAddDesignHandler}
						appearance="subtle"
						spacing="compact"
						testId="issue-design-section.ui.panel.heading.add-design-popup-button"
						icon={AddIcon}
						label={formatMessage(messages.addDesignPopupButtonLabel)}
						interactionName="auto-generated.issue-design-section.ui.heading.add-design-popup-button.button1"
					/>
				)}
				<Tooltip
					content={
						isPanelExpanded
							? formatMessage(messages.collapseTooltip)
							: formatMessage(messages.expandTooltip)
					}
				>
					{(tooltipProps) => (
						<Button
							{...tooltipProps}
							appearance="subtle"
							spacing="none"
							testId="issue-design-section.ui.panel.heading.designs-panel-expansion-button"
							iconBefore={
								isPanelExpanded ? (
									<ChevronUpIcon
										color="currentColor"
										label={formatMessage(messages.collapseButtonLabel)}
									/>
								) : (
									<ChevronDownIcon
										color="currentColor"
										label={formatMessage(messages.expandButtonLabel)}
									/>
								)
							}
							onClick={onDesignsPanelExpansion}
							interactionName="auto-generated.issue-design-section.ui.heading.designs-panel-expansion-button.button3"
						/>
					)}
				</Tooltip>
			</div>
		</div>
	);
};

export default DesignsHeading;

const sectionHeadingIconsStyles = css({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.100', '8px'),
});

const sectionHeadingStyles = css({
	display: 'flex',
	alignItems: 'center',
	flexWrap: 'nowrap',
	justifyContent: 'space-between',
});
